// TopHoldings.js
import React, { Fragment, useState } from 'react';
import i18n from '../../../../i18n';
import '../KeyMetrics.css';
import { useSelector } from 'react-redux';
import HoldingsRow from './HoldingsRow';
import Select from 'react-select';

/* --------------------------- react-select styles --------------------------- */
const customStyles = {
  control: (base) => ({
    ...base,
    borderRadius: 0,
    boxShadow: 'none',
    minHeight: 36,
  }),
  option: (base) => ({
    ...base,
    color: '#5b6770',
    fontSize: 14,
    borderRadius: 0,
    backgroundColor: 'white',
    ':hover': { backgroundColor: '#050139', color: 'white' },
  }),
  menu: (base) => ({ ...base, borderRadius: 0, zIndex: 5 }),
  singleValue: (base, state) => ({
    ...base,
    opacity: state.isDisabled ? 0.5 : 1,
    transition: 'opacity 100ms',
  }),
  dropdownIndicator: (base) => ({ ...base, color: '#050139' }),
};

/* ----------------------------- display settings --------------------------- */
/** What the TABLE should show per fund type. Allowed: 'top10' | 'full' */
const DISPLAY_RULES = {
  ETF: 'top10',
  BMK: 'top10',
  WTETF: 'full',
  WTMOD: 'full',
  DEFAULT: 'full',
};

/* --------------------------------- helpers -------------------------------- */
const getType = (fund) => fund.type ?? fund.propertyType;
const decode = (s = '') => s.replaceAll('&amp;', '&');
const cleanName = (name) => decode((name || '').split('|').pop()).trim();
const toKey = (name) => cleanName(name).toLowerCase();
const capList = (list, rule) => (rule === 'top10' ? list.slice(0, 10) : list);

/* ------------------------------- main component --------------------------- */
const TopHoldings = () => {
  const data = useSelector((state) => state.fundData.data);

  // Search + UI state
  const [query, setQuery] = useState('');
  const [inputText, setInputText] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const [visibleCount, setVisibleCount] = useState(10);

  /* ------------------------------- choose funds ---------------------------- */
  const displayFunds = data.filter(
    (f) => f.assetClass !== 'Fixed Income' && f.topHoldings
  );
  const tickers = displayFunds.map((f) => f.ticker);

  /* -------------------------- build FULL holdings -------------------------- */
  const fullHoldings = displayFunds.map((fund) => {
    const keys = fund.topHoldings?.keys ?? [];
    const values = fund.topHoldings?.values ?? [];
    const len = Math.min(keys.length, values.length);
    return Array.from({ length: len }, (_, i) => ({
      name: keys[i],
      weight: Number(values[i]),
    }));
  });

  /* ------------- presence map (for tooltips on holding hover) -------------- */
  // presenceMap['exxon mobil'] = [{ ticker: 'DLN', weight: 0.023 }, ...]
  const presenceMap = fullHoldings.reduce((map, list, fundIdx) => {
    const ticker = tickers[fundIdx];
    list.forEach((h) => {
      const key = toKey(h.name);
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push({ ticker, weight: Number(h.weight) || 0 });
    });
    return map;
  }, {});

  // Always search FULL holdings; then re-apply per-type caps for display
  const q = query.trim().toLowerCase();
  const filteredHoldings = fullHoldings.map((list, i) => {
    const filtered = q ? list.filter((h) => toKey(h.name).includes(q)) : list;
    const t = getType(displayFunds[i]);
    const rule = DISPLAY_RULES[t] ?? DISPLAY_RULES.DEFAULT;
    return capList(filtered, rule);
  });

  /* ------------------------------ table layout ----------------------------- */
  const maxRows = filteredHoldings.reduce(
    (m, list) => Math.max(m, list.length),
    0
  );
  const rowsToShow = Math.min(visibleCount, maxRows);

  /* --------------------------------- header -------------------------------- */
  const RenderHeader = () => {
    let asOfDate = null;
    if (data[0] && data[0].stdPerformanceMonthly) {
      asOfDate = new Date(
        data[0].stdPerformanceMonthly[0].asOfDate
      ).toLocaleDateString();
    }

    if (displayFunds.length === 0) return null;

    const tickerCells = displayFunds.map((fund, idx) => {
      const borderStyle = idx !== 0 ? 'holdingsBorderleft' : '';
      const isLinkType = !['BMK', 'BLEND', 'COD', 'WTMOD'].includes(fund.type);
      const url = fund.fundPageURL || fund.msUrl;

      return (
        <th
          key={fund.ticker}
          colSpan="2"
          className={`${borderStyle} borderbottom textLeft`}
        >
          {isLinkType ? (
            <a
              className="tickerLinkFC"
              href={url}
              target="_new"
              rel="noreferrer"
            >
              {fund.ticker}
            </a>
          ) : (
            <span>{fund.ticker.toUpperCase()}</span>
          )}
          {/* No tooltip on fund header */}
        </th>
      );
    });

    return (
      <Fragment>
        <tr>
          <th colSpan={tickerCells.length * 2} className="textLeft">
            {i18n.t('top ten holdings').toUpperCase()} {i18n.t('as of')}{' '}
            {asOfDate}{' '}
            <small className="ml-2" style={{ fontWeight: 400 }}>
              • Table uses per-type display rules
            </small>
          </th>
        </tr>
        <tr>{tickerCells}</tr>
      </Fragment>
    );
  };

  /* ---------------------------------- rows --------------------------------- */
  const RenderRows = () => {
    if (displayFunds.length === 0) return null;

    const indexes = Array.from({ length: rowsToShow }, (_, n) => n);
    return (
      <Fragment>
        {indexes.map((_, index) => (
          <HoldingsRow
            key={index}
            fundRows={filteredHoldings} // capped + filtered
            idx={index}
            type="dataRow"
            tickers={tickers}
            presenceMap={presenceMap} // drives cross-fund tooltips
          />
        ))}
      </Fragment>
    );
  };

  /* ---------------------------- see more / less ---------------------------- */
  const handleSeeMore = () => {
    const next = visibleCount < 50 ? 50 : visibleCount + 50;
    setVisibleCount(next);
  };
  const handleSeeLess = () => setVisibleCount(10);

  const showSeeMore = rowsToShow < maxRows;
  const showSeeLess = visibleCount > 10;

  /* --------------------------- select: options list ------------------------ */
  // Build dropdown options from FULL holdings (matches search scope)
  const optionSet = new Set();
  fullHoldings.forEach((list) => {
    list.forEach((h) => {
      const label = cleanName(h.name);
      if (label) optionSet.add(label);
    });
  });
  const options = Array.from(optionSet)
    .sort((a, b) => a.localeCompare(b))
    .map((label) => ({ value: label.toLowerCase(), label }));

  /* ------------------------------- search handlers ------------------------- */
  const resetToFirstPage = () => setVisibleCount(10);

  // Only react to real typing or clearing
  const handleSearchTyping = (value, { action: reason }) => {
    if (
      reason === 'input-blur' ||
      reason === 'menu-close' ||
      reason === 'set-value'
    )
      return;
    setInputText(value);
    setSelectedOption(null);
    setQuery((value || '').toLowerCase());
    resetToFirstPage();
  };

  const handleSearchSelect = (option) => {
    if (!option) {
      setSelectedOption(null);
      setInputText('');
      setQuery('');
    } else {
      setSelectedOption(option);
      setInputText('');
      setQuery(option.value);
    }
    resetToFirstPage();
  };

  /* --------------------------------- render -------------------------------- */
  return (
    <div
      className="col-12"
      style={{ paddingRight: '0px', marginBottom: '65px', paddingLeft: '0px' }}
    >
      {/* Search */}
      <div className="row" style={{ marginBottom: '30px', marginTop: '30px' }}>
        <div className="col-12 col-md-6">
          <Select
            classNamePrefix="react-select"
            placeholder="Search holdings by name"
            isClearable
            options={options}
            styles={customStyles}
            inputValue={inputText}
            onInputChange={handleSearchTyping}
            value={selectedOption}
            onChange={handleSearchSelect}
          />
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table tableHR key-metrics">
          <thead>{RenderHeader()}</thead>
          <tbody>{RenderRows()}</tbody>
        </table>
      </div>

      {/* See more / less */}
      {(showSeeMore || showSeeLess) && (
        <div className="textCenter" style={{ marginTop: '8px' }}>
          {showSeeMore && (
            <button className="btn btn-link p-0" onClick={handleSeeMore}>
              See more
            </button>
          )}
          {showSeeMore && showSeeLess && <span className="mx-2">•</span>}
          {showSeeLess && (
            <button className="btn btn-link p-0" onClick={handleSeeLess}>
              See less
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TopHoldings;
